import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';

const About = () => {
  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="bg-[#2C2416] text-white py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="mb-8">
            <span className="inline-block px-4 py-2 bg-[#3D3426] rounded-full text-sm text-[#C9A961]">
              Our Story
            </span>
          </div>
          <h1 className="text-5xl md:text-7xl font-bold mb-6">
            ABOUT GUAG
          </h1>
          <p className="text-2xl text-gray-300">
            GUAG was built as a ready nutrition solution to a problem that most people overlook.
          </p>
        </div>
      </section>

      {/* The Reality Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="mb-8">
            <span className="inline-block px-4 py-2 bg-[#F5F1E8] rounded-full text-sm text-[#8B6F47] uppercase tracking-wider">
              The Reality
            </span>
          </div>
          <p className="text-2xl text-[#2C2416] mb-8">
            Modern diets are energy-sufficient, but nutritionally thin.
          </p>
          <p className="text-xl text-[#5C4033] mb-6">
            Even people who eat "well" often fall short on key micronutrients that support metabolic health, 
            immune resilience, and daily performance.
          </p>
          <p className="text-2xl font-bold text-[#8B6F47]">
            GUAG exists to close that gap.
          </p>
        </div>
      </section>

      {/* Where It Started Section */}
      <section className="py-20 bg-[#F5F1E8]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="mb-6">
                <span className="inline-block px-4 py-2 bg-white rounded-full text-sm text-[#8B6F47] uppercase tracking-wider">
                  Where It Started
                </span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-[#2C2416] mb-6 leading-tight">
                GUAG began as a practical solution, not a brand exercise.
              </h2>
              <p className="text-lg text-[#5C4033] mb-6 leading-relaxed">
                Years before it became a product, it started as a breakfast made at home — formulated with{' '}
                <strong>less sugar</strong>, <strong>no unnecessary salt</strong>, and a{' '}
                <strong>higher nutritional standard</strong> than what was commercially available at the time.
              </p>
              <p className="text-lg text-[#5C4033] mb-6">
                The goal was simple: food that actually supported the body, not just filled it.
              </p>
              <p className="text-xl font-bold text-[#8B6F47]">
                That same principle still defines GUAG today.
              </p>
            </div>
            <div>
              <img
                src="https://d64gsuwffb70l.cloudfront.net/6851587676ae218610324179_1769273859993_f5040020.png"
                alt="GUAG Origins"
                className="w-full h-auto rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Why It Exists Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 bg-[#F5F1E8] rounded-full text-sm text-[#8B6F47] uppercase tracking-wider mb-6">
              Why It Exists
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-[#2C2416] mb-8">
              The Wake-Up Call
            </h2>
          </div>

          <div className="bg-[#2C2416] text-white rounded-2xl p-12 mb-8">
            <p className="text-xl text-gray-300 mb-6">
              During the global health disruptions of recent years, one reality became impossible to ignore:
            </p>
            <h3 className="text-3xl md:text-4xl font-bold mb-4">
              Nutrition is not optional.
            </h3>
            <h3 className="text-3xl md:text-4xl font-bold text-[#C9A961]">
              It is foundational.
            </h3>
          </div>

          <div className="bg-[#F5F1E8] rounded-2xl p-8 mb-8">
            <p className="text-[#5C4033] mb-4">
              Subclinical deficiencies in nutrients such as
            </p>
            <div className="flex flex-wrap gap-3 mb-6">
              <span className="px-4 py-2 bg-white rounded-full font-semibold text-[#8B6F47]">zinc</span>
              <span className="px-4 py-2 bg-white rounded-full font-semibold text-[#8B6F47]">selenium</span>
              <span className="px-4 py-2 bg-white rounded-full font-semibold text-[#8B6F47]">B-vitamins</span>
            </div>
            <p className="text-[#5C4033]">
              These deficiencies don't always present as illness, but they quietly undermine immune response, 
              recovery capacity, and long-term health.
            </p>
          </div>

          <p className="text-lg text-[#5C4033] mb-4">
            GUAG addresses this — not as a supplement, and not as a sugary cereal — but as a{' '}
            <strong className="text-[#8B6F47]">nutritionally complete breakfast</strong> that fits into real life.
          </p>
          <p className="text-lg text-[#5C4033] mb-2">
            Something you can eat every day.
          </p>
          <p className="text-lg text-[#5C4033]">
            Something your body builds resilience with every day.
          </p>
        </div>

        <div className="max-w-4xl mx-auto px-6 mt-12">
          <img
            src="https://d64gsuwffb70l.cloudfront.net/6851587676ae218610324179_1769273874523_f7a49c81.png"
            alt="GUAG Products"
            className="w-full h-auto rounded-2xl shadow-2xl"
          />
        </div>
      </section>

      {/* What GUAG Is Section */}
      <section className="py-20 bg-[#F5F1E8]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 bg-white rounded-full text-sm text-[#8B6F47] uppercase tracking-wider mb-6">
              What GUAG Is
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-[#2C2416] mb-6">
              A Functional Breakfast
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            <div className="bg-white rounded-xl p-6 text-center">
              <div className="text-4xl font-bold text-[#8B6F47] mb-2">9.5g</div>
              <div className="text-sm text-[#2C2416] font-semibold mb-1">High in Plant Protein</div>
              <div className="text-xs text-[#5C4033]">per 50g serving</div>
            </div>
            <div className="bg-white rounded-xl p-6 text-center">
              <div className="text-lg font-bold text-[#8B6F47] mb-2">Non-GMO</div>
              <div className="text-sm text-[#2C2416] font-semibold mb-1">Ingredients</div>
              <div className="text-xs text-[#5C4033]">Clean, natural sourcing</div>
            </div>
            <div className="bg-white rounded-xl p-6 text-center">
              <div className="text-lg font-bold text-[#8B6F47] mb-2">Plant-Based</div>
              <div className="text-sm text-[#2C2416] font-semibold mb-1">Protein Sources</div>
              <div className="text-xs text-[#5C4033]">Pea protein isolate & soy bean flour</div>
            </div>
            <div className="bg-white rounded-xl p-6 text-center">
              <div className="text-lg font-bold text-[#8B6F47] mb-2">Balanced</div>
              <div className="text-sm text-[#2C2416] font-semibold mb-1">macronutrients</div>
              <div className="text-xs text-[#5C4033]">Optimized ratios</div>
            </div>
            <div className="bg-white rounded-xl p-6 text-center">
              <div className="text-4xl font-bold text-[#8B6F47] mb-2">21+</div>
              <div className="text-sm text-[#2C2416] font-semibold mb-1">Clinically relevant micronutrient coverage</div>
              <div className="text-xs text-[#5C4033]">vitamins & minerals</div>
            </div>
            <div className="bg-white rounded-xl p-6 text-center">
              <div className="text-4xl font-bold text-[#8B6F47] mb-2">4g</div>
              <div className="text-sm text-[#2C2416] font-semibold mb-1">Reduced sugar</div>
              <div className="text-xs text-[#5C4033]">per serving</div>
            </div>
            <div className="bg-white rounded-xl p-6 text-center">
              <div className="text-lg font-bold text-[#8B6F47] mb-2">No added</div>
              <div className="text-sm text-[#2C2416] font-semibold mb-1">salt</div>
              <div className="text-xs text-[#5C4033]">Clean formulation</div>
            </div>
            <div className="bg-white rounded-xl p-6 text-center">
              <div className="text-lg font-bold text-[#8B6F47] mb-2">No unnecessary</div>
              <div className="text-sm text-[#2C2416] font-semibold mb-1">fillers</div>
              <div className="text-xs text-[#5C4033]">Purpose-driven ingredients</div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-8 text-center">
            <p className="text-xl text-[#5C4033] mb-6">
              It works as a warm porridge or a cold shake.
            </p>
            <p className="text-xl text-[#5C4033] mb-6">
              It fits busy mornings without compromising nutritional intent.
            </p>
            <p className="text-2xl font-bold text-[#8B6F47]">
              This is food engineered with purpose.
            </p>
          </div>
        </div>
      </section>

      {/* The GUAG Project Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 bg-[#F5F1E8] rounded-full text-sm text-[#8B6F47] uppercase tracking-wider mb-6">
              The GUAG Project
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-[#2C2416] mb-8">
              Responsibility Matters
            </h2>
          </div>

          <p className="text-xl text-[#5C4033] mb-8 leading-relaxed">
            For every commercial product sold, GUAG supports access to breakfast for those who don't have it. 
            This is executed through the <strong>GUAG Project</strong>, a parallel initiative focused on providing 
            nutritionally meaningful breakfasts where they are most needed.
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="bg-[#F5F1E8] rounded-2xl p-8 text-center">
              <p className="text-lg text-[#5C4033] mb-2">Not as a handout.</p>
              <p className="text-2xl font-bold text-[#8B6F47]">Dignity preserved.</p>
            </div>
            <div className="bg-[#F5F1E8] rounded-2xl p-8 text-center">
              <p className="text-lg text-[#5C4033] mb-2">As a foundation.</p>
              <p className="text-2xl font-bold text-[#8B6F47]">Building futures.</p>
            </div>
          </div>

          <p className="text-2xl font-bold text-center text-[#2C2416] mb-8">
            Because nutrition should not be a privilege.
          </p>

          <div className="text-center">
            <Link
              to="/project"
              className="inline-block px-8 py-4 bg-[#8B6F47] text-white rounded-full hover:bg-[#5C4033] transition-colors font-medium text-lg"
            >
              Learn More About GUAG/PROJECT
            </Link>
          </div>
        </div>
      </section>

      {/* Our Position Section */}
      <section className="py-20 bg-[#2C2416] text-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 bg-[#3D3426] rounded-full text-sm text-[#C9A961] uppercase tracking-wider mb-6">
              Our Position
            </span>
          </div>

          <p className="text-xl text-gray-300 mb-6">
            GUAG does not compete on flavour hype or emotional branding.
          </p>
          <p className="text-xl text-gray-300 mb-8">
            It competes on formulation, high nutritional value, and daily consistency.
          </p>
          <p className="text-xl text-gray-300 mb-8">
            Even if you want a novelty candy breakfast cereal, you might still love GUAG because its 
            natural ingredients and smooth texture taste great too.
          </p>
          <p className="text-2xl font-bold">
            If you're looking for something that works — every morning — GUAG was built for you.
          </p>
        </div>
      </section>

      {/* Join the GUAG Family Section */}
      <section className="py-20 bg-[#F5F1E8]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#2C2416] mb-8">
              Join the GUAG Family
            </h2>
            <p className="text-xl text-[#5C4033]">
              Thousands have made GUAG their daily foundation. Real nutrition. Real results. Real community.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="bg-white rounded-2xl p-8 text-center">
              <div className="mb-6">
                <CheckCircle className="mx-auto text-[#8B6F47]" size={48} />
              </div>
              <h3 className="text-2xl font-bold text-[#2C2416] mb-3">Consistent Quality</h3>
              <p className="text-[#5C4033]">Same formula, every batch. No compromises.</p>
            </div>
            <div className="bg-white rounded-2xl p-8 text-center">
              <div className="mb-6">
                <CheckCircle className="mx-auto text-[#8B6F47]" size={48} />
              </div>
              <h3 className="text-2xl font-bold text-[#2C2416] mb-3">Purpose-Driven</h3>
              <p className="text-[#5C4033]">Every purchase supports those who need it most.</p>
            </div>
            <div className="bg-white rounded-2xl p-8 text-center">
              <div className="mb-6">
                <CheckCircle className="mx-auto text-[#8B6F47]" size={48} />
              </div>
              <h3 className="text-2xl font-bold text-[#2C2416] mb-3">Real Results</h3>
              <p className="text-[#5C4033]">Energy that lasts. Focus that stays.</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link
              to="/nutrition"
              className="px-8 py-4 bg-[#8B6F47] text-white rounded-full hover:bg-[#5C4033] transition-colors font-medium text-lg text-center"
            >
              Shop GUAG — From R59
            </Link>
            <Link
              to="/"
              className="px-8 py-4 bg-white border-2 border-[#8B6F47] text-[#8B6F47] rounded-full hover:bg-[#8B6F47] hover:text-white transition-colors font-medium text-lg text-center"
            >
              Explore Products
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Link
            to="/nutrition"
            className="inline-block px-12 py-5 bg-[#8B6F47] text-white rounded-full hover:bg-[#5C4033] transition-colors font-medium text-xl"
          >
            Start Your Journey
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;